'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useLocale, useTranslations } from 'next-intl'
import { Typography } from '@mui/material'
import dayjs from 'dayjs'
import 'dayjs/locale/ar'
import 'dayjs/locale/en'

import useRedirectToHome from '@/src/hooks/navigation/useRedirectToHome'
import { useTripList } from '@/src/hooks/trips/useTripList'
import BottomToolbarWrapper from '@/src/components/mobile/bottomToolbar'
import FullPageLoader from '@/src/components/mobile/fullPageLoader'
import { NEGATIVE_TRIP_STATUSES, PROGRESS_TRIP_STATUSES } from '@/src/constants/trips'
import { TripListCategory, TripSummary } from '@/src/types/trips'
import { formatTripTitleParts } from '@/src/utils/trips/tripListHelpers'
import { TripList, TripListCard, TripStatusTone } from './tripList'
import { useAppDispatch } from '@/src/redux/store'
import { clearAuthUser, refreshAuthUser } from '@/src/redux/slices/authSlice'
import { useUserLogoutMutation } from '@/src/api/rtkQuery/userApi'
import { getBrandName } from '@/src/utils/buildUtils'
import { checkDeviceType, deleteCookie } from '@/src/utils/browser'
import { getMobileDeviceType } from '@/src/utils/deviceType'

const APP_TRIPS_SESSION_KEY = 'isAppTrips'

const SignInModalWrapper = dynamic(() => import('../../../common/header/variants/flyin/SignInModal/SignInModalWrapper'), {
    ssr: false
})

const TripListPageWrapper: React.FC<{ isMobile?: boolean; isMultiBrand?: boolean; isAppTrips?: boolean }> = ({
    isMultiBrand: isMultiBrandSsr,
    isAppTrips
}) => {
    const dispatch = useAppDispatch()
    const isMultiBrand = isMultiBrandSsr ?? false
    const t = useTranslations('tripList')
    const locale = useLocale()
    const isRtl = locale === 'ar'
    const goToHome = useRedirectToHome()
    const [isMounted, setIsMounted] = useState(false)
    const [isAppExperience, setIsAppExperience] = useState(false)
    const [isNativeApp, setIsNativeApp] = useState(false)
    const [activeTab, setActiveTab] = useState<TripListCategory>('upcoming')
    const [isSignInOpen, setIsSignInOpen] = useState(false)
    const [triggerLogout] = useUserLogoutMutation()
    const hasHandledUnauthorizedRef = useRef(false)
    const scrollPositionRef = useRef<number>(0)
    const previousCardsLengthRef = useRef<number>(0)

    const {
        isLoggedIn,
        isLoading,
        isFetching,
        isError,
        hasUnauthorizedError,
        tripsByCategory,
        tripCounts,
        hasMore,
        loadMore,
        isLoadingMore,
        refetch
    } = useTripList({
        isMultiBrand
    })

    useEffect(() => {
        if (typeof window === 'undefined') return
        const hasQueryAppFlag = isAppTrips === true
        if (hasQueryAppFlag) window.sessionStorage.setItem(APP_TRIPS_SESSION_KEY, 'true')
        const hasSessionAppFlag = window.sessionStorage.getItem(APP_TRIPS_SESSION_KEY) === 'true'
        setIsAppExperience(hasQueryAppFlag || hasSessionAppFlag)
        const mobileDeviceType = getMobileDeviceType()
        setIsNativeApp(mobileDeviceType === 'AndroidApp' || mobileDeviceType === 'iPhoneApp')
        setIsMounted(true)
    }, [isAppTrips])

    useEffect(() => {
        if (isMounted && !isLoggedIn) {
            setIsSignInOpen(true)
        }
    }, [isMounted, isLoggedIn])

    useEffect(() => {
        if (!isMounted || !isLoggedIn || !hasUnauthorizedError || hasHandledUnauthorizedRef.current) {
            return
        }
        hasHandledUnauthorizedRef.current = true

        const triggerLoginForCurrentPlatform = () => {
            const deviceType = checkDeviceType()
            if (deviceType === 'AndroidApp') {
                window?.MobileApp?.GO_TO_LOGIN?.('')
            } else if (typeof window?.webkit?.messageHandlers['GO_TO_LOGIN'] === 'object') {
                window.webkit.messageHandlers['GO_TO_LOGIN'].postMessage('')
            } else {
                setIsSignInOpen(true)
            }
        }

        const forceSignOutOnUnauthorized = async () => {
            try {
                await triggerLogout({ brand: getBrandName() }).unwrap()
            } catch (error) {
                console.error('Trip list unauthorized logout failed:', error)
            } finally {
                dispatch(clearAuthUser())
                if (typeof window !== 'undefined') {
                    const domain = '.' + window.location.hostname
                    deleteCookie('usermisc', domain)
                    deleteCookie('userid', domain)
                    deleteCookie('userData', domain)
                }
                dispatch(refreshAuthUser())
                if (isAppExperience || isNativeApp) {
                    triggerLoginForCurrentPlatform()
                } else {
                    setIsSignInOpen(true)
                }
            }
        }

        void forceSignOutOnUnauthorized()
    }, [dispatch, hasUnauthorizedError, isAppExperience, isLoggedIn, isMounted, isNativeApp, triggerLogout])

    const handleSignInClose = useCallback(() => {
        setIsSignInOpen(false)
    }, [setIsSignInOpen])

    const formatTripDateParts = useCallback(
        (travelDate?: string | null): { dateDay: string; dateMonth: string; dateLabel: string } => {
            if (!travelDate) {
                return { dateDay: '--', dateMonth: '', dateLabel: t('dateUnknown') }
            }
            try {
                const parsed = dayjs(travelDate.replace('+0000', 'Z'))
                if (!parsed.isValid()) {
                    return { dateDay: '--', dateMonth: '', dateLabel: t('dateUnknown') }
                }
                dayjs.locale(locale)
                const localized = parsed.locale(locale)
                return {
                    dateDay: localized.format('DD'),
                    dateMonth: localized.format('MMM'),
                    dateLabel: localized.format('DD MMM YYYY')
                }
            } catch {
                return { dateDay: '--', dateMonth: '', dateLabel: t('dateUnknown') }
            }
        },
        [locale, t]
    )

    const getStatusTone = useCallback((status?: string): TripStatusTone => {
        const normalized = status?.toLowerCase().trim() || ''
        if (NEGATIVE_TRIP_STATUSES.includes(normalized)) return 'error'
        if (PROGRESS_TRIP_STATUSES.includes(normalized)) return 'warning'
        return normalized ? 'success' : 'default'
    }, [])

    const mapTripsToCards = useCallback(
        (list: TripSummary[]): TripListCard[] =>
            list.map(trip => {
                const dateParts = formatTripDateParts(trip.travelDate)
                return {
                    id: trip.tripId,
                    tripId: trip.tripId,
                    titleParts: formatTripTitleParts(trip, t('untitledTrip', { tripId: trip.tripId })),
                    subtitle: trip.travellers || t('travellerFallback'),
                    bookedByLabel: trip.bookingSource === 'LINKED' ? t('bookedByCleartrip') : undefined,
                    bookingSource: trip.bookingSource,
                    dateDay: dateParts.dateDay,
                    dateMonth: dateParts.dateMonth,
                    dateLabel: dateParts.dateLabel,
                    statusLabel: trip.bookingStatus || '',
                    statusTone: getStatusTone(trip.bookingStatus)
                }
            }),
        [formatTripDateParts, getStatusTone, t]
    )

    const cards = useMemo(() => {
        const selectedTrips = tripsByCategory[activeTab] || []
        return mapTripsToCards(selectedTrips)
    }, [activeTab, mapTripsToCards, tripsByCategory])

    // Maintain scroll position after new data is loaded
    useEffect(() => {
        // Only restore scroll if we just finished loading more (cards increased)
        if (
            !isLoadingMore[activeTab] &&
            previousCardsLengthRef.current > 0 &&
            cards.length > previousCardsLengthRef.current &&
            scrollPositionRef.current > 0
        ) {
            // Small delay to ensure DOM is updated with new cards
            const timeoutId = setTimeout(() => {
                if (typeof window !== 'undefined') {
                    // Restore the scroll position to where it was before loading
                    window.scrollTo({
                        top: scrollPositionRef.current,
                        behavior: 'auto'
                    })
                    // Reset refs
                    scrollPositionRef.current = 0
                    previousCardsLengthRef.current = 0
                }
            }, 100)

            return () => clearTimeout(timeoutId)
        }
    }, [isLoadingMore, activeTab, cards.length])

    const tabConfig = useMemo(
        () => [
            { value: 'upcoming' as TripListCategory, label: t('tabs.upcoming'), count: tripCounts.upcoming },
            { value: 'past' as TripListCategory, label: t('tabs.past'), count: tripCounts.past }
        ],
        [t, tripCounts]
    )

    const handleRetry = useCallback(() => {
        refetch()
    }, [refetch])

    const handleCardClick = useCallback((tripId: string, bookingSource?: 'MAIN' | 'LINKED') => {
        if (tripId) {
            const query = bookingSource === 'LINKED' ? '?account=LINKED' : ''
            window.location.href = `/accounts/trips/${tripId}${query}`
        }
    }, [])

    const handleLoadMore = useCallback(() => {
        // Store current scroll position and cards length before loading
        if (typeof window !== 'undefined') {
            scrollPositionRef.current = window.scrollY || window.pageYOffset
            previousCardsLengthRef.current = cards.length
        }
        loadMore(activeTab)
    }, [activeTab, loadMore, cards.length])

    if (!isMounted) return null

    const showFullPageLoader = isLoading && isLoggedIn && cards.length === 0
    const showListSkeleton = isLoading && !isLoadingMore[activeTab]

    // Show full page loader on initial load when logged in
    if (showFullPageLoader) {
        return (
            <>
                <FullPageLoader
                    hideBackdrop
                    message={
                        <>
                            <Typography
                                variant="body1"
                                sx={{ color: 'text.primary', textAlign: 'center', fontWeight: 500 }}
                            >
                                {t('fetchingTrips')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                {t('fetchingTripsMessage')}
                            </Typography>
                        </>
                    }
                />
                {isSignInOpen && <SignInModalWrapper open={isSignInOpen} onClose={handleSignInClose} />}
            </>
        )
    }

    return (
        <>
            <TripList
                heading={t('title')}
                isRtl={isRtl}
                hideHeader={isAppExperience}
                onBack={() => goToHome({ isReloadRequired: false })}
                tabs={tabConfig}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                cards={cards}
                isLoading={showListSkeleton}
                isError={isError}
                errorLabel={t('error')}
                retryLabel={t('retry')}
                onRetry={handleRetry}
                emptyState={{
                    title: isLoggedIn ? t('empty.title') : t('notSignedIn.title'),
                    description: isLoggedIn ? t('empty.description') : t('notSignedIn.description'),
                    actionLabel: isLoggedIn ? (isAppExperience || isNativeApp ? '' : t('empty.cta')) : t('notSignedIn.cta'),
                    onAction: isLoggedIn ? () => goToHome({ isReloadRequired: false }) : () => setIsSignInOpen(true)
                }}
                showSignInCta={!isLoggedIn}
                signInCtaLabel={t('notSignedIn.cta')}
                onSignIn={() => setIsSignInOpen(true)}
                onCardClick={handleCardClick}
                hasMore={hasMore[activeTab]}
                isLoadingMore={isLoadingMore[activeTab]}
                onLoadMore={handleLoadMore}
                loadMoreLabel={t('loadMore')}
            />

            {!isAppExperience && <BottomToolbarWrapper />}

            {isSignInOpen && <SignInModalWrapper open={isSignInOpen} onClose={handleSignInClose} />}
        </>
    )
}

export default TripListPageWrapper
export { TripListPageWrapper }
